// Date & Time Architecture Standard · PD-031 · BR-DATE-001..003
// Calendar dates are timezone-independent. Never call `new Date()` on a
// calendar-only value because JavaScript would parse it as midnight UTC
// and then re-render it in the browser's local timezone — that's the
// exact off-by-one bug PD-031 exists to prevent.

const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Parse a `YYYY-MM-DD` string into `{y, m, d}` numbers without ever
 * involving JavaScript's Date object. Returns null for anything else. */
export function parseCalendarDate(value) {
  if (!value || typeof value !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  return { y: +m[1], m: +m[2], d: +m[3] };
}

/** Coerce any legacy value (ISO datetime, `YYYY-MM-DDTHH:mm:ssZ`, or a
 * bare calendar date) to a `YYYY-MM-DD` string, or null. */
export function toCalendarDate(value) {
  if (value == null || value === "") return null;
  if (typeof value !== "string") {
    if (value instanceof Date) {
      // Read the date parts in UTC to avoid tz shifting.
      const y = value.getUTCFullYear();
      const mo = String(value.getUTCMonth() + 1).padStart(2, "0");
      const d = String(value.getUTCDate()).padStart(2, "0");
      return `${y}-${mo}-${d}`;
    }
    return null;
  }
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const head = trimmed.split("T")[0].split(" ")[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : null;
}

/** Format `YYYY-MM-DD` as "October 25, 2026" (default) or shorter forms.
 * `format`: 'long' · 'short' · 'medium' · 'iso'.
 * NEVER shifts the value by timezone. */
export function formatCalendarDate(value, format = "medium") {
  const parts = parseCalendarDate(toCalendarDate(value));
  if (!parts) return "—";
  const { y, m, d } = parts;
  const mi = m - 1;
  if (format === "iso") return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  if (format === "short") return `${MONTHS_SHORT[mi]} ${d}, ${y}`;
  if (format === "medium") return `${MONTHS_SHORT[mi]} ${d}, ${y}`;
  return `${MONTHS_LONG[mi]} ${d}, ${y}`;
}

/** Same but omits the year when both dates are in the same year. */
export function formatCalendarRange(from, to, format = "medium") {
  const a = parseCalendarDate(toCalendarDate(from));
  const b = parseCalendarDate(toCalendarDate(to));
  if (!a || !b) return "—";
  const sameYear = a.y === b.y;
  const left = sameYear
    ? `${(format === "long" ? MONTHS_LONG : MONTHS_SHORT)[a.m - 1]} ${a.d}`
    : formatCalendarDate(toCalendarDate(from), format);
  const right = formatCalendarDate(toCalendarDate(to), format);
  return `${left} – ${right}`;
}

/** Whole-day delta (b - a). Both must be YYYY-MM-DD; returns null otherwise. */
export function daysBetween(a, b) {
  const A = parseCalendarDate(toCalendarDate(a));
  const B = parseCalendarDate(toCalendarDate(b));
  if (!A || !B) return null;
  // Use UTC midpoint math so DST cannot corrupt the delta.
  const ta = Date.UTC(A.y, A.m - 1, A.d);
  const tb = Date.UTC(B.y, B.m - 1, B.d);
  return Math.round((tb - ta) / 86400000);
}

/** Today, expressed as a calendar date in the browser's local timezone
 * (used for countdowns "days until sailing"). This IS a user-facing
 * concept and is allowed to be locale-influenced.
 * Storage code should use the backend's `today_utc_date()` instead. */
export function todayLocalDate() {
  const n = new Date();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, "0");
  const d = String(n.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Compute stage from calendar dates only. Mirrors backend `voyage_stage_for`. */
export function voyageStageFor(nowDate, embarkDate, disembarkDate) {
  const N = parseCalendarDate(nowDate);
  const E = parseCalendarDate(embarkDate);
  const D = parseCalendarDate(disembarkDate);
  if (!N || !E || !D) return "unknown";
  const nt = Date.UTC(N.y, N.m - 1, N.d);
  const et = Date.UTC(E.y, E.m - 1, E.d);
  const dt = Date.UTC(D.y, D.m - 1, D.d);
  if (nt < et) return "pre-voyage";
  if (nt <= dt) return "in-voyage";
  return "post-voyage";
}
